// src/pages/BulkImport.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../store';
import { postsAPI } from '../utils/api';
import { getPlatform } from '../utils/platforms';

const CSV_TEMPLATE = `caption,scheduled_at,tags,status
"Your post caption here","2026-07-01 09:00","#tag1 #tag2","queued"
"Another post caption","2026-07-02 10:00","#tag3","draft"
"Third post without schedule","","","draft"
`;

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]);
  return lines.slice(1).map((line, idx) => {
    const values = parseCSVLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h.trim()] = (values[i] || '').trim(); });
    row._row = idx + 2;
    return row;
  }).filter(r => r.caption);
}

function parseCSVLine(line) {
  const result = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'postflow_bulk_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const VALID_STATUSES = ['queued', 'draft', 'pending_review'];

export default function BulkImport() {
  const navigate = useNavigate();
  const { accounts, fetchAccounts, fetchSummary } = useStore();
  const [rows, setRows] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=done
  const fileRef = useRef();
  const dropRef = useRef();

  useEffect(() => { fetchAccounts(); }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) return toast.error('Please upload a CSV file');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (!parsed.length) return toast.error('No valid rows found in CSV');
      const errs = [];
      parsed.forEach(r => {
        if (!r.caption) errs.push(`Row ${r._row}: caption is required`);
        if (r.status && !VALID_STATUSES.includes(r.status)) {
          errs.push(`Row ${r._row}: status must be queued, draft, or pending_review`);
        }
        if (r.scheduled_at && isNaN(Date.parse(r.scheduled_at))) {
          errs.push(`Row ${r._row}: invalid date format (use YYYY-MM-DD HH:MM)`);
        }
      });
      setErrors(errs);
      setRows(parsed);
      setStep(2);
    };
    reader.readAsText(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove('drag-over');
    handleFile(e.dataTransfer.files[0]);
  };

  const toggleAccount = (id) => {
    setSelectedAccounts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImport = async () => {
    if (errors.length) return toast.error('Fix validation errors before importing');
    if (!rows.length) return toast.error('No posts to import');
    setImporting(true);
    try {
      const posts = rows.map(r => ({
        caption: r.caption,
        scheduled_at: r.scheduled_at ? new Date(r.scheduled_at).toISOString() : null,
        status: VALID_STATUSES.includes(r.status) ? r.status : 'draft',
        tags: r.tags ? r.tags.split(/\s+/).filter(Boolean) : [],
        account_ids: selectedAccounts,
      }));
      await postsAPI.bulkCreate({ posts });
      fetchSummary();
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => { setRows([]); setErrors([]); setFileName(''); setSelectedAccounts([]); setStep(1); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <div>
          <span className="page-title">Bulk Import</span>
          <div className="page-subtitle">Import multiple posts at once via CSV</div>
        </div>
        <button className="btn btn-outline" onClick={() => navigate('/posts/manage')}>← Back to Posts</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

        {/* Step indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24, maxWidth: 560 }}>
          {[['1', 'Upload CSV'], ['2', 'Preview & Accounts'], ['3', 'Done']].map(([num, label], i) => (
            <React.Fragment key={num}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: step > i + 1 ? '#22c55e' : step === i + 1 ? '#0f766e' : '#e8eaf0', color: step >= i + 1 ? '#fff' : '#8892a4', flexShrink: 0 }}>
                  {step > i + 1 ? '✓' : num}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: step === i + 1 ? '#1a1f2e' : '#8892a4' }}>{label}</span>
              </div>
              {i < 2 && <div style={{ flex: 1, height: 1, background: step > i + 1 ? '#22c55e' : '#e8eaf0', margin: '0 10px', maxWidth: 60 }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div style={{ maxWidth: 560 }}>
            <div style={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 8, padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>CSV Format</div>
              <div style={{ fontSize: 12, color: '#5a6578', marginBottom: 12, lineHeight: 1.6 }}>
                Your CSV must include a <code style={{ background: '#f0fdfa', padding: '1px 5px', borderRadius: 3 }}>caption</code> column.
                Optional: <code style={{ background: '#f0fdfa', padding: '1px 5px', borderRadius: 3 }}>scheduled_at</code> (YYYY-MM-DD HH:MM),{' '}
                <code style={{ background: '#f0fdfa', padding: '1px 5px', borderRadius: 3 }}>tags</code> (space-separated),{' '}
                <code style={{ background: '#f0fdfa', padding: '1px 5px', borderRadius: 3 }}>status</code> (queued / draft / pending_review).
              </div>
              <button className="btn btn-outline" onClick={downloadTemplate} style={{ fontSize: 12 }}>
                ⬇ Download Template
              </button>
            </div>

            <div
              ref={dropRef}
              onDragOver={e => { e.preventDefault(); dropRef.current?.classList.add('drag-over'); }}
              onDragLeave={() => dropRef.current?.classList.remove('drag-over')}
              onDrop={onDrop}
              onClick={() => fileRef.current.click()}
              style={{ border: '2px dashed #c8d0e0', borderRadius: 10, padding: '48px 24px', textAlign: 'center', cursor: 'pointer', background: '#fafbfd', transition: 'border-color 0.15s' }}
            >
              <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1f2e', marginBottom: 4 }}>Drop your CSV file here</div>
              <div style={{ fontSize: 12, color: '#8892a4' }}>or click to browse</div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && (
          <div style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e' }}>
                {rows.length} post{rows.length !== 1 ? 's' : ''} parsed from <span style={{ color: '#0f766e' }}>{fileName}</span>
              </div>
              <button className="btn btn-ghost" style={{ fontSize: 11, marginLeft: 'auto' }} onClick={reset}>✕ Change File</button>
            </div>

            {errors.length > 0 && (
              <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c', marginBottom: 6 }}>Validation Errors ({errors.length})</div>
                {errors.map((e, i) => <div key={i} style={{ fontSize: 11, color: '#b91c1c', marginBottom: 2 }}>• {e}</div>)}
              </div>
            )}

            <div style={{ display: 'flex', gap: 16 }}>
              {/* Preview table */}
              <div style={{ flex: 1, overflowX: 'auto' }}>
                <table className="data-table" style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e8eaf0' }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Caption</th>
                      <th>Schedule</th>
                      <th>Tags</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td style={{ color: '#8892a4' }}>{r._row}</td>
                        <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.caption}</td>
                        <td style={{ whiteSpace: 'nowrap', color: r.scheduled_at ? '#1a1f2e' : '#aaa' }}>{r.scheduled_at || '—'}</td>
                        <td style={{ color: '#0f766e', fontSize: 11 }}>{r.tags || '—'}</td>
                        <td><span className={`badge badge-${r.status || 'draft'}`}>{r.status || 'draft'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Account selector */}
              <div style={{ width: 220, flexShrink: 0 }}>
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">Publish to Accounts</span>
                  </div>
                  <div className="card-body" style={{ padding: '8px 10px', maxHeight: 320, overflowY: 'auto' }}>
                    {accounts.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center', padding: '12px 0' }}>No accounts connected</div>
                    ) : (
                      accounts.map(a => {
                        const pl = getPlatform(a.platform);
                        const checked = selectedAccounts.includes(a.id);
                        return (
                          <div key={a.id} onClick={() => toggleAccount(a.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer', borderRadius: 5, background: checked ? '#f0fdfa' : 'transparent' }}>
                            <div className="platform-icon" style={{ background: pl.bg, color: pl.color, width: 22, height: 22, flexShrink: 0 }}>{pl.icon}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#1a1f2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.account_name}</div>
                              <div style={{ fontSize: 10, color: '#8892a4', textTransform: 'capitalize' }}>{a.platform}</div>
                            </div>
                            <div style={{ marginLeft: 'auto', width: 14, height: 14, border: `2px solid ${checked ? '#0f766e' : '#c8d0e0'}`, borderRadius: 3, background: checked ? '#0f766e' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 9, color: '#fff' }}>
                              {checked ? '✓' : ''}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div style={{ padding: '8px 10px', borderTop: '1px solid #f0f1f5', fontSize: 11, color: '#8892a4' }}>
                    {selectedAccounts.length} selected
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', fontSize: 12 }}
                    onClick={handleImport}
                    disabled={importing || errors.length > 0}
                  >
                    {importing ? 'Importing…' : `Import ${rows.length} Post${rows.length !== 1 ? 's' : ''}`}
                  </button>
                  <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', fontSize: 12, marginTop: 6 }} onClick={reset}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 3 && (
          <div className="empty-state" style={{ paddingTop: 80 }}>
            <span style={{ fontSize: 52 }}>🎉</span>
            <h3>Import Complete!</h3>
            <p>{rows.length} post{rows.length !== 1 ? 's' : ''} imported successfully{selectedAccounts.length ? ` to ${selectedAccounts.length} account${selectedAccounts.length !== 1 ? 's' : ''}` : ''}.</p>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn btn-primary" onClick={() => navigate('/posts/manage')}>View Posts</button>
              <button className="btn btn-outline" onClick={reset}>Import More</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
